import React, { useMemo, useState, useEffect, useCallback, useLayoutEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table';
import osLogo from '../os-logo.png';
import { computeCalendarPrice, computeDiagonalPrice, computeVerticalDebitPrice } from '../utils/tos';
import {
  formatDatePart,
  calculateDTE,
  formatSpread,
  formatFinancialValueCell
} from '../utils/formatters';

// ---------- Helpers ----------
const customNumericSort = (rowA, rowB, columnId) => {
  const valA = rowA.getValue(columnId);
  const valB = rowB.getValue(columnId);
  const numA = (valA === null || valA === undefined) ? -Infinity : Number(valA);
  const numB = (valB === null || valB === undefined) ? -Infinity : Number(valB);
  if (numA < numB) return -1;
  if (numA > numB) return 1;
  return 0;
};

// ---------- Icons (Feather-style) ----------
const FilterIcon = ({ isActive }) => (
  <svg
    width="13" height="13" viewBox="0 0 24 24"
    fill={isActive ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden
  >
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
  </svg>
);

const SortIcon = ({ state }) => {
  if (state === 'asc') {
    return (
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden
      >
        <polyline points="18 15 12 9 6 15"></polyline>
      </svg>
    );
  }
  if (state === 'desc') {
    return (
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden
      >
        <polyline points="6 9 12 15 18 9"></polyline>
      </svg>
    );
  }
  return (
    <svg width="11" height="14" viewBox="0 0 11 14" fill="none"
      stroke="currentColor" strokeWidth="1.5"
      strokeLinecap="round" strokeLinejoin="round"
      aria-hidden style={{ opacity: 0.45 }}
    >
      <polyline points="2 5 5.5 1.5 9 5"></polyline>
      <polyline points="2 9 5.5 12.5 9 9"></polyline>
    </svg>
  );
};

// ---------- Filter popover ----------
const FilterInputs = ({ column, onClose }) => {
  const currentFilter = column.getFilterValue();
  const [minValue, setMinValue] = useState(currentFilter?.[0] ?? '');
  const [maxValue, setMaxValue] = useState(currentFilter?.[1] ?? '');

  const applyFilter = useCallback(() => {
    const min = minValue !== '' && !isNaN(Number(minValue)) ? Number(minValue) : undefined;
    const max = maxValue !== '' && !isNaN(Number(maxValue)) ? Number(maxValue) : undefined;
    if (min === undefined && max === undefined) {
      column.setFilterValue(undefined);
      return;
    }
    column.setFilterValue([min, max]);
  }, [minValue, maxValue, column]);

  const handleClear = () => {
    setMinValue('');
    setMaxValue('');
    column.setFilterValue(undefined);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      applyFilter();
      onClose();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  const hasFilter = column.getIsFiltered() || minValue !== '' || maxValue !== '';

  return (
    <div
      className="filter-popover"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="filter-popover-header">
        <div className="filter-popover-title">Filter range</div>
        <button type="button" className="filter-popover-close" onClick={onClose} aria-label="Close">×</button>
      </div>
      <div className="filter-popover-row">
        <input
          type="number"
          value={minValue}
          onChange={(e) => setMinValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Min"
          className="filter-popover-input"
          step="any"
          autoFocus
        />
        <span className="filter-popover-dash">–</span>
        <input
          type="number"
          value={maxValue}
          onChange={(e) => setMaxValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Max"
          className="filter-popover-input"
          step="any"
        />
      </div>
      <div className="filter-popover-actions">
        <button
          type="button"
          onClick={handleClear}
          disabled={!hasFilter}
          className="filter-popover-btn filter-popover-btn-secondary"
        >
          Clear
        </button>
        <button
          type="button"
          onClick={() => { applyFilter(); onClose(); }}
          className="filter-popover-btn filter-popover-btn-primary"
        >
          Apply
        </button>
      </div>
    </div>
  );
};

const DTEFilterComponent = ({ column, distinctDTEs, onClose }) => {
  const currentFilter = column.getFilterValue();

  const handleChange = (event) => {
    const value = event.target.value;
    column.setFilterValue(value === "ALL_DTE_VALUES" ? undefined : value);
    onClose();
  };

  const handleClear = () => {
    column.setFilterValue(undefined);
    onClose();
  };

  const hasFilter = currentFilter !== undefined && currentFilter !== null && currentFilter !== '';

  return (
    <div
      className="filter-popover"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="filter-popover-header">
        <div className="filter-popover-title">Filter DTE</div>
        <button type="button" className="filter-popover-close" onClick={onClose} aria-label="Close">×</button>
      </div>
      <select
        value={currentFilter === undefined ? "ALL_DTE_VALUES" : currentFilter}
        onChange={handleChange}
        onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } }}
        className="filter-popover-select"
        autoFocus
      >
        <option value="ALL_DTE_VALUES">All DTEs</option>
        {distinctDTEs.map(dte => (
          <option key={dte} value={dte}>
            {dte === 'N/A' ? 'N/A' : dte}
          </option>
        ))}
      </select>
      <div className="filter-popover-actions">
        <button
          type="button"
          onClick={handleClear}
          disabled={!hasFilter}
          className="filter-popover-btn filter-popover-btn-secondary"
        >
          Clear
        </button>
      </div>
    </div>
  );
};

// ---------- Filter fns ----------
const customNumberRange = (row, columnId, filterValue) => {
  const raw = row.getValue(columnId);
  let value = typeof raw === 'number' ? raw : parseFloat(raw);
  if (isNaN(value)) return false;
  value = value * 100;
  const [min, max] = filterValue || [];
  const minNum = Number(min);
  const maxNum = Number(max);
  if (!isNaN(minNum) && min !== undefined && min !== '') { if (value < minNum) return false; }
  if (!isNaN(maxNum) && max !== undefined && max !== '') { if (value > maxNum) return false; }
  return true;
};

const hedgeFilterFn = (row, columnId, filterValue) => {
  const raw = row.getValue(columnId);
  let value = typeof raw === 'number' ? raw : parseFloat(raw);
  if (isNaN(value)) return false;
  // Mirror the cell renderer's sign-inversion: negative raw = profit (positive display),
  // positive raw = cost (negative display). Then ×100 for dollar display.
  value = (value < 0 ? Math.abs(value) : -value) * 100;
  const [min, max] = filterValue || [];
  const minNum = Number(min);
  const maxNum = Number(max);
  if (!isNaN(minNum) && min !== undefined && min !== '') { if (value < minNum) return false; }
  if (!isNaN(maxNum) && max !== undefined && max !== '') { if (value > maxNum) return false; }
  return true;
};

const getColumnBgColor = (columnId) => {
  if (columnId === 'calendar_spread' || columnId.startsWith('cal_short_delta') || columnId === 'iv_skew') {
    return 'var(--col-blue-bg)';
  }
  if (columnId === 'diagonal_spread' || columnId.startsWith('diag_short_delta')) {
    return 'var(--col-orange-bg)';
  }
  return 'transparent';
};

// ---------- Tooltip helper ----------
const Th = ({ label, tooltip }) => (
  <span title={tooltip || label} className="th-label">{label}</span>
);

// ---------- Star icon ----------
const StarIcon = ({ filled }) => (
  filled ? (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>
      <polygon
        points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"
        fill="currentColor"
      />
    </svg>
  ) : (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden>
      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
    </svg>
  )
);

// Format "pinned N min ago" for stale row tooltips
const formatAgo = (ts) => {
  if (!ts) return '';
  const diffSec = Math.max(0, (Date.now() - ts) / 1000);
  if (diffSec < 60) return 'just now';
  const m = Math.floor(diffSec / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.floor(h / 24);
  return `${d} d ago`;
};

// ---------- Main component ----------
const FusionTable = ({
  data,
  pinnedRows = [],
  isPinned,
  togglePin,
  clearAllPins,
  pinCount = 0,
  setSelectedCombination,
  selectedCombinationId,
  sorting,
  onSortingChange,
  columnFilters,
  onColumnFiltersChange,
  showOS = true,
  showTOS = true,
  onAnalysisDataUpdate,
  hideBody = false,
  tableMetricMode,
  setTableMetricMode,
  alertHighlightedRowIds = new Set()
}) => {
  const [activeFilterInputForColumnId, setActiveFilterInputForColumnId] = useState(null);
  const [filterAlign, setFilterAlign] = useState('left');
  const filterInputContainerRef = React.useRef(null);
  const tableContainerRef = React.useRef(null);

  const toggleMetricMode = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setTableMetricMode(tableMetricMode === 'delta' ? 'price' : 'delta');
  }, [tableMetricMode, setTableMetricMode]);

  const columns = useMemo(() => {
    const dteCol = {
      accessorFn: row => {
        const firstExp = row.calendar_spread?.legs[0]?.expiration;
        return calculateDTE(firstExp);
      },
      header: () => <Th label="DTE" tooltip="Days To Expiration — of the short (near) leg" />,
      id: 'dte',
      cell: info => {
        const dteValue = info.getValue();
        const row = info.row.original;
        const calSpread = row.calendar_spread;
        let expirationString = 'N/A';
        if (calSpread && calSpread.legs && calSpread.legs.length >= 2) {
          const firstExp = calSpread.legs[0]?.expiration;
          const secondExp = calSpread.legs[1]?.expiration;
          expirationString = `${formatDatePart(firstExp)}/${formatDatePart(secondExp)}`;
        }
        return (
          <div style={{ lineHeight: '1.1' }}>
            {dteValue === 'N/A' ? dteValue : Number(dteValue)}
            <br />
            <span style={{ fontSize: '0.7em', color: '#666' }}>
              {expirationString}
            </span>
          </div>
        );
      },
      size: 90,
      sortingFn: customNumericSort,
      enableColumnFilter: true,
      filterFn: (row, columnId, filterValue) => {
        const rowValue = row.getValue(columnId);
        if (filterValue === undefined || filterValue === null || filterValue === '') return true;
        return String(rowValue) === String(filterValue);
      },
    };

    const calendarSpreadCol = {
      accessorFn: row => formatSpread(row.calendar_spread, 'calendar', row.calendar_quantity),
      header: () => <Th label="Calendar" tooltip="Calendar spread — short near-dated, long far-dated (same strike)" />,
      id: 'calendar_spread',
      cell: info => {
        const row = info.row.original;
        const price = computeCalendarPrice(row);
        const text = info.getValue();
        return (
          <span title={price !== null && price !== undefined ? `Price: ${Number(price).toFixed(2)}` : 'Price: N/A'}>
            {text}
          </span>
        );
      },
      size: 150,
    };

    const calShortDeltaCol = {
      accessorFn: row => {
        if (tableMetricMode === 'price') {
          const price = computeCalendarPrice(row);
          return price !== null ? price * 100 : 'N/A';
        }
        const delta = row.calendar_spread?.legs?.[0]?.delta;
        return typeof delta === 'number' ? delta * 100 : 'N/A';
      },
      header: () => (
        <Th
          label={tableMetricMode === 'delta' ? 'Sh. Δ' : 'Price'}
          tooltip={tableMetricMode === 'delta'
            ? 'Short-leg delta (×100). Click ⇄ to show price instead.'
            : 'Calendar spread price. Click ⇄ to show short-leg delta.'}
        />
      ),
      id: 'cal_short_delta',
      cell: info => {
        const value = info.getValue();
        if (value === 'N/A' || value === null) return 'N/A';
        const isPrice = tableMetricMode === 'price';
        const digits = isPrice ? 2 : 0;
        return <span>{formatFinancialValueCell(value, false, digits, isPrice)}</span>;
      },
      size: 80,
      sortingFn: customNumericSort,
      filterFn: 'inNumberRange',
      enableColumnFilter: true,
    };

    const ivSkewCol = {
      accessorFn: row => {
        const calSpread = row.calendar_spread;
        if (!calSpread || !calSpread.legs || calSpread.legs.length < 2) return 'N/A';
        const frontIV = calSpread.legs[0]?.iv;
        const backIV = calSpread.legs[1]?.iv;
        if (typeof frontIV === 'number' && typeof backIV === 'number' && frontIV !== 0) {
          return 100 * (1 - (backIV / frontIV));
        }
        return 'N/A';
      },
      header: () => <Th label="IV Skew" tooltip="Implied volatility skew: 1 − (backIV / frontIV), in %" />,
      id: 'iv_skew',
      cell: info => {
        const value = info.getValue();
        if (value === 'N/A') return 'N/A';
        return `${formatFinancialValueCell(value, false, 2)}%`;
      },
      size: 80,
      sortingFn: customNumericSort,
      filterFn: 'inNumberRange',
      enableColumnFilter: false,
    };

    const diagonalSpreadCol = {
      accessorFn: row => formatSpread(row.diagonal_spread, 'diagonal', row.diagonal_quantity),
      header: () => <Th label="Diagonal" tooltip="Diagonal spread — short near-dated, long far-dated (different strikes)" />,
      id: 'diagonal_spread',
      cell: info => {
        const row = info.row.original;
        const price = computeDiagonalPrice(row);
        const text = info.getValue();
        return (
          <span title={price !== null && price !== undefined ? `Price: ${Number(price).toFixed(2)}` : 'Price: N/A'}>
            {text}
          </span>
        );
      },
      size: 150,
    };

    const diagShortDeltaCol = {
      accessorFn: row => {
        if (tableMetricMode === 'price') {
          const price = computeDiagonalPrice(row);
          return price !== null ? price * 100 : 'N/A';
        }
        const delta = row.diagonal_spread?.legs?.[0]?.delta;
        return typeof delta === 'number' ? delta * 100 : 'N/A';
      },
      header: () => (
        <Th
          label={tableMetricMode === 'delta' ? 'Sh. Δ' : 'Price'}
          tooltip={tableMetricMode === 'delta'
            ? 'Short-leg delta (×100). Click ⇄ to show price instead.'
            : 'Diagonal spread price. Click ⇄ to show short-leg delta.'}
        />
      ),
      id: 'diag_short_delta',
      cell: info => {
        const value = info.getValue();
        if (value === 'N/A' || value === null) return 'N/A';
        const isPrice = tableMetricMode === 'price';
        const digits = isPrice ? 2 : 0;
        return <span>{formatFinancialValueCell(value, false, digits, isPrice)}</span>;
      },
      size: 80,
      sortingFn: customNumericSort,
      filterFn: 'inNumberRange',
      enableColumnFilter: true,
    };

    const verticalSpreadCol = {
      accessorFn: row => formatSpread(row.vertical_spread, 'vertical'),
      header: () => <Th label="Vert. Hedge" tooltip="Put vertical used as crash hedge — bought / sold strikes" />,
      id: 'vertical_spread',
      cell: info => {
        const row = info.row.original;
        const price = computeVerticalDebitPrice(row);
        const text = info.getValue();
        return (
          <span title={price !== null && price !== undefined ? `Price: ${Number(price).toFixed(2)}` : 'Price: N/A'}>
            {text}
          </span>
        );
      },
      size: 150,
    };

    const strikeDifferenceCol = {
      accessorFn: row => {
        const verticalShortStrike = row.vertical_spread?.legs?.[0]?.strike || 0;
        const diagonalShortStrike = row.diagonal_spread?.legs?.[0]?.strike || 0;
        return Math.abs(verticalShortStrike - diagonalShortStrike);
      },
      header: () => <Th label="Diff" tooltip="Distance between the vertical short strike and the diagonal short strike" />,
      id: 'strike_difference',
      cell: info => info.getValue(),
      sortingFn: customNumericSort,
      filterFn: 'inNumberRange',
      enableColumnFilter: true,
    };

    const netCostCol = {
      accessorFn: row => row.costs?.net_strategy_cost_after_vertical_credit,
      header: () => <Th label="Hedge" tooltip="If the market crashes, this is the fixed profit of the strategy" />,
      id: 'net_cost',
      cell: info => {
        let originalValue = info.getValue();
        let color = 'inherit';
        let valueToDisplay = originalValue;
        if (typeof originalValue === 'number' && originalValue !== 0) {
          if (originalValue < 0) { color = 'green'; valueToDisplay = Math.abs(originalValue); }
          else { color = 'red'; valueToDisplay = -originalValue; }
        }
        return <span style={{ color }}>{formatFinancialValueCell(valueToDisplay, true, 0)}</span>;
      },
      sortingFn: customNumericSort,
      filterFn: 'hedgeFilterFn',
      enableColumnFilter: true,
    };

    const netDeltaCol = {
      accessorKey: 'net_delta',
      header: () => <Th label="Net Δ" tooltip="Net delta of the combined position" />,
      id: 'net_delta',
      cell: info => {
        const value = info.getValue();
        let color = 'inherit';
        let displayValue = 'N/A';
        if (typeof value === 'number') {
          displayValue = value.toFixed(2);
          if (value > 0) color = 'green';
          else if (value < 0) color = 'red';
        } else if (value !== null && value !== undefined) {
          const numValue = parseFloat(value);
          if (!isNaN(numValue)) {
            displayValue = numValue.toFixed(2);
            if (numValue > 0) color = 'green';
            else if (numValue < 0) color = 'red';
          } else {
            displayValue = String(value);
          }
        }
        return <span style={{ color }}>{displayValue}</span>;
      },
      sortingFn: customNumericSort,
      filterFn: 'inNumberRange',
      enableColumnFilter: true,
    };

    const vodCol = {
      accessorKey: 'min_pl_death_valley',
      header: () => <Th label="VoD" tooltip="Valley of Death — minimum P/L between the strikes" />,
      id: 'min_pl_death_valley',
      cell: info => {
        let originalValue = info.getValue();
        let numValueForColoring;
        if (originalValue === null || originalValue === undefined || originalValue === '') numValueForColoring = NaN;
        else if (typeof originalValue === 'number') numValueForColoring = originalValue;
        else numValueForColoring = parseFloat(originalValue);
        let color = 'inherit';
        if (!isNaN(numValueForColoring) && numValueForColoring !== 0) {
          color = numValueForColoring > 0 ? 'green' : 'red';
        }
        return <span style={{ color }}>{formatFinancialValueCell(originalValue, true, 0)}</span>;
      },
      sortingFn: customNumericSort,
      filterFn: 'customNumberRange',
      filterVariant: 'custom',
      enableColumnFilter: true,
    };

    const costCol = {
      accessorFn: row => row.total_debit,
      header: () => <Th label="Cost" tooltip="Total debit paid for the combined position" />,
      id: 'total_debit',
      cell: info => <span>{formatFinancialValueCell(info.getValue(), true, 0)}</span>,
      sortingFn: customNumericSort,
      filterFn: 'customNumberRange',
      enableColumnFilter: true,
    };

    const rrCol = {
      accessorFn: row => row.rr_ratio,
      header: () => <Th label="R/R" tooltip="Risk / Reward ratio in percent (higher is better)" />,
      id: 'rr_ratio',
      cell: info => `${info.getValue()}%`,
      sortingFn: customNumericSort,
      filterFn: 'inNumberRange',
      enableColumnFilter: true,
    };

    const osCol = showOS ? {
      header: () => <Th label="OS" tooltip="Open this combination in OptionStrat" />,
      id: 'optionStratLink',
      accessorKey: 'optionStratLink',
      cell: info => {
        const link = info.getValue();
        if (link) {
          return (
            <a href={link} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <img src={osLogo} alt="OptionStrat" style={{ width: '20px', height: 'auto' }} />
            </a>
          );
        }
        return <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#aaa' }}>N/A</span>;
      },
      enableSorting: false,
      enableColumnFilter: false,
    } : null;

    const tosCol = showTOS ? {
      header: () => <Th label="TOS" tooltip="Download ThinkOrSwim-compatible order file" />,
      id: 'tos_export',
      cell: ({ row }) => {
        const d = row.original;
        const getFirstExpiry = (item) => {
          const exps = [];
          const pushExp = (e) => { if (e) exps.push(e); };
          (item?.calendar_spread?.legs || []).forEach(l => pushExp(l?.expiration));
          (item?.diagonal_spread?.legs || []).forEach(l => pushExp(l?.expiration));
          (item?.vertical_spread?.legs || []).forEach(l => pushExp(l?.expiration));
          const dates = exps.map(s => { const dd = new Date(s); return Number.isNaN(dd.getTime()) ? null : dd; }).filter(Boolean);
          if (dates.length === 0) return null;
          return new Date(Math.min.apply(null, dates));
        };
        const formatYyyymmdd = (dd) => `${dd.getFullYear()}${String(dd.getMonth() + 1).padStart(2, '0')}${String(dd.getDate()).padStart(2, '0')}`;
        const handleDownload = (e) => {
          e.stopPropagation();
          import('../utils/tos').then(mod => {
            const content = mod.buildTosFileContent(d);
            if (!content) return;
            const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const firstExp = getFirstExpiry(d);
            const suffix = firstExp ? `_${formatYyyymmdd(firstExp)}` : '';
            a.download = `fusion${suffix}.txt`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          });
        };
        return (
          <button
            onClick={handleDownload}
            title="Download TOS-compatible order file"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: '28px', height: '24px', borderRadius: '6px',
              border: 'none', background: 'transparent', cursor: 'pointer'
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <img src="https://www.opcioguru.com/static/trading2/img/thinkorswim.png" alt="TOS" style={{ width: '20px', height: 'auto' }} />
          </button>
        );
      },
      size: 56,
      enableSorting: false,
      enableColumnFilter: false,
    } : null;

    const pinCol = {
      id: 'pin_star',
      header: () => <Th label="" tooltip="Pin a combination to keep it at the top across refreshes" />,
      cell: ({ row }) => {
        const r = row.original;
        const pinned = typeof isPinned === 'function' ? isPinned(r) : !!r._pinned;
        const staleAgo = r._stale ? formatAgo(r._pinnedAt) : '';
        const title = pinned
          ? (r._stale ? `Pinned ${staleAgo} — stale snapshot. Click to unpin.` : 'Click to unpin')
          : 'Pin this combination';
        return (
          <button
            type="button"
            className={`pin-btn ${pinned ? 'is-pinned' : ''}`}
            onMouseDown={(e) => { e.stopPropagation(); }}
            onClick={(e) => { e.stopPropagation(); if (typeof togglePin === 'function') togglePin(r); }}
            title={title}
            aria-pressed={pinned}
            aria-label={pinned ? 'Unpin' : 'Pin'}
          >
            <StarIcon filled={pinned} />
          </button>
        );
      },
      size: 32,
      enableSorting: false,
      enableColumnFilter: false,
    };

    // Flat structure — column background colors already indicate the groups.
    return [
      pinCol,
      dteCol,
      calendarSpreadCol, calShortDeltaCol, ivSkewCol,
      diagonalSpreadCol, diagShortDeltaCol,
      verticalSpreadCol, strikeDifferenceCol,
      netCostCol, netDeltaCol, vodCol, costCol, rrCol,
      osCol, tosCol,
    ].filter(Boolean);
  }, [showOS, showTOS, tableMetricMode, isPinned, togglePin]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting, columnFilters },
    onSortingChange,
    onColumnFiltersChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    sortTypes: { customNumeric: customNumericSort },
    filterFns: { customNumberRange, hedgeFilterFn },
  });

  // ---------- Keyboard nav ----------
  const handleKeyDown = useCallback((e) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    const unpinnedRows = table.getRowModel().rows;
    // Build a combined array of rows for navigation
    const allVisibleRows = [
      ...pinnedRows.map(pr => ({ original: pr, isPinned: true })),
      ...unpinnedRows
    ];
    if (allVisibleRows.length === 0) return;
    
    const currentIndex = selectedCombinationId
      ? allVisibleRows.findIndex(row => row.original.id === selectedCombinationId)
      : -1;
    let nextIndex = currentIndex;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      nextIndex = currentIndex < allVisibleRows.length - 1 ? currentIndex + 1 : currentIndex;
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      nextIndex = currentIndex > 0 ? currentIndex - 1 : currentIndex;
    }
    
    if (nextIndex !== currentIndex && allVisibleRows[nextIndex]) {
      const nextId = allVisibleRows[nextIndex].original.id;
      setSelectedCombination(allVisibleRows[nextIndex].original);
      
      // Scroll into view immediately
      if (tableContainerRef.current) {
        const selectedRowElement = tableContainerRef.current.querySelector(`[data-row-id="${nextId}"]`);
        if (selectedRowElement) {
          const container = tableContainerRef.current;
          const rowRect = selectedRowElement.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const thead = container.querySelector('thead');
          const headerHeight = thead ? thead.getBoundingClientRect().height : 0;
          
          const isVisible = (rowRect.top + 1) >= (containerRect.top + headerHeight) && (rowRect.bottom - 1) <= containerRect.bottom;
          
          if (!isVisible) {
            if (rowRect.top < containerRect.top + headerHeight) {
              container.scrollTop -= (containerRect.top + headerHeight) - rowRect.top;
            } else {
              container.scrollTop += rowRect.bottom - containerRect.bottom;
            }
          }
        }
      }
    }
  }, [table, selectedCombinationId, setSelectedCombination, pinnedRows]);

  useEffect(() => {
    const handleClick = (e) => {
      if (e.target.closest('.filter-inputs') || e.target.closest('.filter-popover')) return;
      if (tableContainerRef.current) tableContainerRef.current.focus();
    };
    const tableContainer = tableContainerRef.current;
    if (tableContainer) tableContainer.addEventListener('click', handleClick);
    return () => { if (tableContainer) tableContainer.removeEventListener('click', handleClick); };
  }, []);

  useLayoutEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
      // Double check in next frame to defeat browser scroll restoration
      requestAnimationFrame(() => {
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollTop = 0;
        }
      });
    }
  }, []);

  useEffect(() => {
    const visibleData = table.getRowModel().rows.map(row => row.original);
    const allSelectableIds = new Set([
      ...pinnedRows.map(item => item.id),
      ...visibleData.map(item => item.id),
    ]);
    if (!allSelectableIds.has(selectedCombinationId)) {
      const firstRow = pinnedRows.length > 0 ? pinnedRows[0] : (visibleData.length > 0 ? visibleData[0] : null);
      if (firstRow) {
        setSelectedCombination(firstRow);
      }
    }
    if (typeof onAnalysisDataUpdate === 'function') onAnalysisDataUpdate(visibleData);
  }, [table.getRowModel().rows, selectedCombinationId, setSelectedCombination, onAnalysisDataUpdate, pinnedRows]);

  // Close filter on outside click
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterInputContainerRef.current &&
          !filterInputContainerRef.current.contains(event.target) &&
          !event.target.closest('.filter-toggle-button')) {
        setActiveFilterInputForColumnId(null);
      }
    };
    if (activeFilterInputForColumnId) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeFilterInputForColumnId]);

  // Auto-flip the filter popover if it would overflow the right edge of the table
  useLayoutEffect(() => {
    if (!activeFilterInputForColumnId) return;
    // Reset first so we can measure the natural (left-aligned) position
    setFilterAlign('left');
    const raf = requestAnimationFrame(() => {
      const el = filterInputContainerRef.current;
      const container = tableContainerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const containerRight = container
        ? container.getBoundingClientRect().right
        : (window.innerWidth || document.documentElement.clientWidth);
      if (rect.right > containerRight - 4) {
        setFilterAlign('right');
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [activeFilterInputForColumnId]);


  const distinctDTEs = useMemo(() => {
    if (!data) return [];
    const dtes = data.map(row => {
      const firstExp = row.calendar_spread?.legs[0]?.expiration;
      return calculateDTE(firstExp);
    });
    return [...new Set(dtes)].sort((a, b) => {
      if (a === 'N/A' && b !== 'N/A') return -1;
      if (b === 'N/A' && a !== 'N/A') return 1;
      if (a === 'N/A' && b === 'N/A') return 0;
      return Number(a) - Number(b);
    });
  }, [data]);

  // ---------- Render ----------
  const headerGroups = table.getHeaderGroups();
  const allLeafColumns = table.getAllLeafColumns();
  const FILTER_NUM_IDS = new Set(['net_cost', 'net_delta', 'min_pl_death_valley', 'total_debit', 'strike_difference', 'cal_short_delta', 'diag_short_delta', 'rr_ratio']);
  const TOGGLE_METRIC_IDS = new Set(['cal_short_delta', 'diag_short_delta']);

  // Build a minimal TanStack-like cell context for a pinned row (which is
  // rendered outside the main getRowModel()). Supports both call patterns
  // used by our column cell renderers: `info.getValue()` and `info.row.original`.
  const makeCellContext = (pinnedRow, column, columns) => {
    const getValueFor = (col, sourceRow) => {
      const def = col.columnDef;
      if (typeof def.accessorFn === 'function') return def.accessorFn(sourceRow);
      const key = def.accessorKey || col.id;
      return sourceRow ? sourceRow[key] : undefined;
    };
    const rowProxy = {
      id: pinnedRow.id,
      original: pinnedRow,
      getValue: (cId) => {
        const col = columns.find(c => c.id === cId);
        return col ? getValueFor(col, pinnedRow) : undefined;
      },
    };
    const getValue = () => getValueFor(column, pinnedRow);
    return {
      getValue,
      renderValue: getValue,
      row: rowProxy,
      column,
      table,
      cell: {},
    };
  };

  return (
    <div
      className="table-container"
      ref={tableContainerRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      style={{
        height: '100%',
        overflow: 'auto',
        outline: 'none',
        position: 'relative',
        backgroundColor: 'var(--bg)'
      }}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead style={{ position: 'sticky', top: 0, zIndex: 20 }}>
          {headerGroups.map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const canSort = header.column.getCanSort();
                const isFilterable = FILTER_NUM_IDS.has(header.column.id) || header.column.id === 'dte';
                const colBg = getColumnBgColor(header.column.id);
                const bgClass = colBg === 'var(--col-blue-bg)'
                  ? 'col-blue'
                  : colBg === 'var(--col-orange-bg)'
                    ? 'col-orange'
                    : '';

                return (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className={`fusion-th ${bgClass}`.trim()}
                    style={{
                      width: header.getSize() !== 150 ? `${header.getSize()}px` : undefined,
                    }}
                  >
                    <div className="fusion-th-row">
                      <div
                        className={`fusion-th-title ${canSort ? 'is-sortable' : ''}`}
                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {canSort && header.column.getIsSorted() && (
                          <span className="fusion-th-sort" aria-hidden>
                            <SortIcon state={header.column.getIsSorted()} />
                          </span>
                        )}
                      </div>

                      {TOGGLE_METRIC_IDS.has(header.column.id) && (
                        <button
                          type="button"
                          className="fusion-th-metric-toggle"
                          onClick={toggleMetricMode}
                          title="Toggle Delta / Price"
                          aria-label="Toggle metric mode"
                        >
                          ⇄
                        </button>
                      )}

                      {isFilterable && (
                        <button
                          type="button"
                          className={`fusion-th-filter-btn filter-toggle-button ${header.column.getIsFiltered() ? 'is-active' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveFilterInputForColumnId(
                              activeFilterInputForColumnId === header.column.id ? null : header.column.id
                            );
                          }}
                          title={header.column.getIsFiltered() ? 'Filter active — click to edit' : 'Add filter'}
                          aria-label="Filter column"
                        >
                          <FilterIcon isActive={header.column.getIsFiltered()} />
                        </button>
                      )}
                    </div>

                    {activeFilterInputForColumnId === header.column.id && (
                      <div
                        ref={filterInputContainerRef}
                        className={`filter-inputs filter-popover-wrap align-${filterAlign}`}
                      >
                        {FILTER_NUM_IDS.has(header.column.id) ? (
                          <FilterInputs
                            column={header.column}
                            onClose={() => setActiveFilterInputForColumnId(null)}
                          />
                        ) : header.column.id === 'dte' ? (
                          <DTEFilterComponent
                            column={header.column}
                            distinctDTEs={distinctDTEs}
                            onClose={() => setActiveFilterInputForColumnId(null)}
                          />
                        ) : null}
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        {!hideBody && (
          <tbody>
            {pinnedRows.length > 0 && (
              <>
                <tr className="pin-divider-row" aria-hidden>
                  <td colSpan={allLeafColumns.length}>
                    <div className="pin-divider-inner">
                      <span className="pin-divider-label">
                        <span className="star-icon" aria-hidden>★</span>
                        Pinned ({pinnedRows.length})
                      </span>
                      {typeof clearAllPins === 'function' && (
                        <button
                          type="button"
                          className="pin-clear-all"
                          onClick={(e) => { e.stopPropagation(); clearAllPins(); }}
                          title="Unpin all"
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {pinnedRows.map((pRow) => (
                  <tr
                    key={pRow.id}
                    data-row-id={pRow.id}
                    onClick={() => setSelectedCombination(pRow)}
                    className={`row-pinned ${pRow._stale ? 'row-stale' : ''} ${selectedCombinationId === pRow.id ? 'selected-row' : ''} ${alertHighlightedRowIds.has(pRow.id) ? 'alert-highlight' : ''}`.trim()}
                    style={{ cursor: 'pointer' }}
                    title={pRow._stale ? `Stale snapshot — pinned ${formatAgo(pRow._pinnedAt)}` : undefined}
                  >
                    {allLeafColumns.map((col) => {
                      const info = makeCellContext(pRow, col, allLeafColumns);
                      const isNumeric = ['net_cost','net_delta','min_pl_death_valley','total_debit','strike_difference','cal_short_delta','diag_short_delta','rr_ratio','iv_skew'].includes(col.id);
                      const isStrikes = ['calendar_spread','diagonal_spread','vertical_spread'].includes(col.id);
                      const colBg = getColumnBgColor(col.id);
                      const bgClass = colBg === 'var(--col-blue-bg)'
                        ? 'col-blue'
                        : colBg === 'var(--col-orange-bg)'
                          ? 'col-orange'
                          : '';
                      const cellDef = col.columnDef.cell;
                      const content = typeof cellDef === 'function' ? cellDef(info) : info.getValue();
                      return (
                        <td
                          key={col.id}
                          className={`${isNumeric ? 'numeric-cell' : ''} ${isStrikes ? 'strikes-cell' : ''} ${bgClass}`.trim()}
                        >
                          {content}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </>
            )}
            {table.getRowModel().rows.map(row => (
              <tr
                key={row.id}
                data-row-id={row.original.id}
                onClick={() => setSelectedCombination(row.original)}
                className={`
                  ${selectedCombinationId === row.original.id ? 'selected-row' : ''}
                  ${alertHighlightedRowIds.has(row.original.id) ? 'alert-highlight' : ''}
                `.trim()}
                style={{ cursor: 'pointer' }}
              >
                {row.getVisibleCells().map(cell => {
                  const isNumeric = ['net_cost','net_delta','min_pl_death_valley','total_debit','strike_difference','cal_short_delta','diag_short_delta','rr_ratio','iv_skew'].includes(cell.column.id);
                  const isStrikes = ['calendar_spread','diagonal_spread','vertical_spread'].includes(cell.column.id);
                  const colBg = getColumnBgColor(cell.column.id);
                  const bgClass = colBg === 'var(--col-blue-bg)'
                    ? 'col-blue'
                    : colBg === 'var(--col-orange-bg)'
                      ? 'col-orange'
                      : '';
                  return (
                    <td
                      key={cell.id}
                      className={`${isNumeric ? 'numeric-cell' : ''} ${isStrikes ? 'strikes-cell' : ''} ${bgClass}`.trim()}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        )}
      </table>
    </div>
  );
};

export default FusionTable;
